/**
 * Custom product form — pipeline edition (propChange, getProp, ProductFormPropUI).
 * Same <product-form-custom> tag as legacy product-form-custom.js — load only ONE of them.
 * Used by custom-product-orchestrator / main-shaped sections.
 */
if (!customElements.get('product-form-custom')) {
  // Constants
  const ELEMENT_NAME = 'product-form-custom';
  const SELECTOR_FORM = 'form';
  const SELECTOR_ID_INPUT = '[name=id]';
  const SELECTOR_SUBMIT_BUTTON = '[type="submit"]';
  const SELECTOR_COLOR_INPUT = '[data-value-type="color"]';
  const SELECTOR_SIZE_INPUT = '[data-value-type="size"]';
  /** Any variant/mockup prop control: data-value-type="color|size|…" */
  const SELECTOR_PROP_INPUT = '[data-value-type]';
  const SELECTOR_COLOR_LABEL = '[data-color-label]';
  const SELECTOR_VARIANT_SELECTS = 'variant-selects';
  const SELECTOR_FORM_BTN = '[data-form-btn]';
  const SELECTOR_FORM_BTN_SUBMIT = '[data-form-btn="submit"]';
  const SELECTOR_VARIANT_LABEL = '[data-form-variant-label]';
  const SELECTOR_VPC_CARD_INPUT = 'input[type="radio"]';
  const SELECTOR_CART_NOTIFICATION = 'cart-notification';
  const SELECTOR_CART_DRAWER = 'cart-drawer';
  const SELECTOR_QUICK_ADD_MODAL = 'quick-add-modal';
  const SELECTOR_LOADING_SPINNER = '.loading__spinner';
  const SELECTOR_ERROR_WRAPPER = '.product-form__error-message-wrapper';
  const SELECTOR_ERROR_MESSAGE = '.product-form__error-message';
  const SELECTOR_SOLD_OUT_MESSAGE = '.sold-out-message';
  const SELECTOR_SPAN = 'span';

  const ATTR_ARIA_HASPOPUP = 'aria-haspopup';
  const ATTR_ARIA_DISABLED = 'aria-disabled';
  const ATTR_DATA_VALUE = 'data-value';
  const ATTR_DATA_VALUE_TYPE = 'data-value-type';
  const ATTR_DATA_COLOR_VALUE = 'data-color-value';
  const ATTR_DATA_PRICE = 'data-price';
  const ATTR_DATA_SHOW_PRICE_IN_BUTTON = 'data-show-price-in-button';
  const ATTR_DATA_AUTO_DISCOUNT = 'data-auto-discount';
  const ATTR_DATA_ORIGINAL_PRICE = 'data-original-price';
  const ATTR_DATA_SHOW_ORIGINAL_PRICE_IN_BUTTON = 'data-show-original-price-in-button';
  const ATTR_DISABLED = 'disabled';
  const ATTR_HIDDEN = 'hidden';
  const ATTR_DATA_HIDE_ERRORS = 'data-hideErrors';

  const CLASS_LOADING = 'loading';
  const CLASS_HIDDEN = 'hidden';
  const CLASS_DISABLED = 'disabled';
  const CLASS_SWATCH_ACTIVE = 'swatch-active';
  const CLASS_IS_EMPTY = 'is-empty';
  const CLASS_BUTTON_PRICE = 'button-price';

  // Kept for ProductFormCustom.CHANGE_TYPES / detail.changeType string stability
  const CHANGE_TYPES = {
    FORM: 'formChange',
    CUSTOM_DATA: 'customDataChange',
    UNKNOWN: 'unknown'
  };

  const propChangeType = (prop) => (prop ? `${prop}Change` : CHANGE_TYPES.UNKNOWN);

  // Event names
  const EVENT_CHANGE = 'productFormCustom:change';
  /** Prop contract: { type: `${prop}Change`, prop, value, source? } */
  const EVENT_PROP_CHANGE = 'productFormCustom:propChange';

  customElements.define(
    ELEMENT_NAME,
    class ProductFormCustom extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector(SELECTOR_FORM);
        if (!this.form) return;
        
        const variantIdInput = this.form.querySelector(SELECTOR_ID_INPUT);
        if (variantIdInput) {
          variantIdInput.disabled = false;
        }
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector(SELECTOR_CART_NOTIFICATION) || document.querySelector(SELECTOR_CART_DRAWER);
        this.submitButton = this.querySelector(SELECTOR_SUBMIT_BUTTON);
        if (this.submitButton) {
          this.submitButtonText = this.submitButton.querySelector(SELECTOR_SPAN);
          if (document.querySelector(SELECTOR_CART_DRAWER)) {
            this.submitButton.setAttribute(ATTR_ARIA_HASPOPUP, 'dialog');
          }
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.customData = {};
        /** Cart props from orch: { formKey, required, asBlob } */
        this.formBlobKeys = new Set();
        this.assetsConfig = [];
      }

      /**
       * Called from orchestrator init.
       * assets: [{ formKey, required?, asBlob? }] — no roles; orch owns ASSET_DEFS
       */
      setAssets(assets) {
        const blobKeys = new Set();
        const list = [];
        if (Array.isArray(assets)) {
          for (const a of assets) {
            if (!a?.formKey) continue;
            const formKey = String(a.formKey);
            const asBlob = a.asBlob === true;
            list.push({
              formKey,
              required: a.required === true,
              asBlob
            });
            if (asBlob) blobKeys.add(formKey);
          }
        }
        this.formBlobKeys = blobKeys;
        this.assetsConfig = list;
        return true;
      }

      getAssets() {
        return this.assetsConfig.slice();
      }

      /** formKeys of assets with required:true that are empty */
      missingRequiredAssets() {
        const missing = [];
        for (const a of this.assetsConfig) {
          if (!a.required) continue;
          const value = this.getCustomData(a.formKey);
          if (value == null || value === '') missing.push(a.formKey);
        }
        return missing;
      }

      hasRequiredAssets() {
        return this.missingRequiredAssets().length === 0;
      }

      connectedCallback() {
        if (this.form) {
          this.form.addEventListener('change', (e) => {
            // Prop inputs emit propChange separately; avoid duplicate generic change
            if (e.target?.hasAttribute?.(ATTR_DATA_VALUE_TYPE)) return;
            this.dispatchChangeEvent(CHANGE_TYPES.FORM);
          });
        }

        // data-value-type → propChange; orchestrator decides mockupProps vs sizePreview
        $(this).on('change', SELECTOR_PROP_INPUT, (e, eventData) => {
          const $target = $(e.target);
          const prop = String($target.attr(ATTR_DATA_VALUE_TYPE) || '').trim();
          const value = $target.attr(ATTR_DATA_VALUE);
          if (!prop || value == null || value === '') return;

          const detail = {
            type: propChangeType(prop),
            prop,
            value,
            source: eventData?.source || null
          };

          // Mockup widget key may live on label/card data-size (≠ commerce data-value)
          if (prop === 'size') {
            const fromDom = String(
              $target.closest('[data-size]').attr('data-size')
              || $(`label[for="${$target.attr('id')}"]`).attr('data-size')
              || ''
            ).trim();
            detail.sizeKey = fromDom || String(value).trim();
          }

          this.dispatchPropChange(detail);
        });

        // Swatch / label active state — product-form-prop-ui.js
        window.ProductFormPropUI?.bind?.(this);
      }

      /** productFormCustom:propChange → { type, prop, value, sizeKey?, source? } */
      dispatchPropChange(detail = {}) {
        const prop = detail.prop || null;
        const value = detail.value ?? null;
        document.dispatchEvent(new CustomEvent(EVENT_PROP_CHANGE, {
          bubbles: true,
          detail: {
            type: detail.type || propChangeType(prop),
            prop,
            value,
            sizeKey: detail.sizeKey ?? null,
            source: detail.source ?? null
          }
        }));
      }

      /** Generic form snapshot event (non-prop). Prefer propChange for mockup props. */
      dispatchChangeEvent(changeType = CHANGE_TYPES.UNKNOWN) {
        if (!this.form) return;

        const formData = new FormData(this.form);
        const data = {};

        for (const [key, value] of formData.entries()) {
          if (data[key]) {
            if (Array.isArray(data[key])) {
              data[key].push(value);
            } else {
              data[key] = [data[key], value];
            }
          } else {
            data[key] = value;
          }
        }

        const variantId = this.variantIdInput ? this.variantIdInput.value : null;

        document.dispatchEvent(new CustomEvent(EVENT_CHANGE, {
          bubbles: true,
          detail: {
            changeType,
            formId: this.form.id || null,
            variantId,
            currentColor: this.getCurrentColor(),
            currentSize: this.getCurrentSize(),
            formData: data,
            customData: { ...this.customData },
            timestamp: new Date().toISOString()
          }
        }));
      }

      static get EVENTS() {
        return {
          CHANGE: EVENT_CHANGE,
          PROP_CHANGE: EVENT_PROP_CHANGE
        };
      }

      static get CHANGE_TYPES() {
        return { ...CHANGE_TYPES };
      }

      // ============================================================================
      // Custom Data Management
      // ============================================================================
      setCustomData(key, value) {
        if (typeof key !== 'string') return false;
        this.customData[key] = value;
        this.dispatchChangeEvent(CHANGE_TYPES.CUSTOM_DATA);
        return true;
      }

      getCustomData(key) {
        if (typeof key !== 'string') return null;
        return this.customData[key] || null;
      }

      clearCustomData(key) {
        if (key) {
          if (typeof key === 'string' && this.customData.hasOwnProperty(key)) {
            delete this.customData[key];
            this.dispatchChangeEvent(CHANGE_TYPES.CUSTOM_DATA);
            return true;
          }
          return false;
        }
        this.customData = {};
        this.dispatchChangeEvent(CHANGE_TYPES.CUSTOM_DATA);
        return true;
      }

      // Image reads by asBlob flag from setAssets (share / legacy helpers).
      getMockup() {
        const asset = this.assetsConfig.find((a) => a.asBlob);
        return asset ? this.getCustomData(asset.formKey) : null;
      }

      getOriginalImage() {
        const asset = this.assetsConfig.find((a) => !a.asBlob && a.required)
          || this.assetsConfig.find((a) => !a.asBlob);
        return asset ? this.getCustomData(asset.formKey) : null;
      }

      clearImageData() {
        for (const a of this.assetsConfig) {
          if (a.formKey) this.clearCustomData(a.formKey);
        }
        return true;
      }

      // ============================================================================
      // Form Submission
      // ============================================================================
      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton || this.submitButton.getAttribute(ATTR_ARIA_DISABLED) === 'true') return;

        const missingAssets = this.missingRequiredAssets();
        if (missingAssets.length) {
          console.warn('[custom-product-form-v2] submit blocked — missing required assets:', missingAssets);
          return;
        }

        this.submitButton.setAttribute(ATTR_ARIA_DISABLED, true);
        this.submitButton.classList.add(CLASS_LOADING);
        this.querySelector(SELECTOR_LOADING_SPINNER).classList.remove(CLASS_HIDDEN);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (window.cartAlwaysNewLineItem) {
          formData.set('properties[_line_item_uid]', window.getCartLineItemUid());
        }
        
        // Helper function to convert data URL to blob
        const dataURLtoBlob = (dataURI, type = 'image/png') => {
          if (!dataURI || typeof dataURI !== 'string' || !dataURI.includes(',')) {
            return null;
          }
          try {
            const byteString = atob(dataURI.split(',')[1]);
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteString.length; i++) {
              ia[i] = byteString.charCodeAt(i);
            }
            // Detect MIME type from data URL if available
            const mimeMatch = dataURI.match(/data:([^;]+)/);
            const mimeType = mimeMatch ? mimeMatch[1] : type;
            return new Blob([ab], { type: mimeType });
          } catch (e) {
            console.error('Error converting data URL to blob:', e);
            return null;
          }
        };
        
        // asBlob assets: data URL → blob; others: URL/string property
        Object.keys(this.customData).forEach(key => {
          const value = this.customData[key];
          if (value === null || value === undefined) return;

          const asBlob = this.formBlobKeys.has(key)
            && typeof value === 'string'
            && value.startsWith('data:');

          if (asBlob) {
            const blob = dataURLtoBlob(value);
            if (blob) {
              formData.append(`properties[${key}]`, blob, `${key.replace(/^_/, '') || 'mockup'}.png`);
            } else {
              formData.append(`properties[${key}]`, String(value));
            }
          } else {
            formData.append(`properties[${key}]`, String(value));
          }
        });

        if (this.cart) {
          const sectionsToRender = this.cart.getSectionsToRender().map((section) => section.id);
          // Include cart-icon-bubble for cart notification
          sectionsToRender.push('cart-icon-bubble');
          formData.append('sections', [...new Set(sectionsToRender)].join(','));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        } else {
          // If no cart element, still request cart-icon-bubble for cart notification
          formData.append('sections', 'cart-icon-bubble');
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.error = true;
              return;
            }
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.addedToCart, {
                source: 'product-form-custom',
                productVariantId: formData.get('id'),
                cartData: response,
              }).catch((err) => {
                console.error('[ProductForm] Error publishing addedToCart event:', err);
              });
              publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form-custom',
                  productVariantId: formData.get('id'),
                  cartData: response,
                }).then(() => {
                    CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);

                }).catch((err) => {
                  console.error('[ProductForm] Error publishing cartUpdate event:', err);
                }); 
              }
            }

            this.error = false;
  
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove(CLASS_LOADING);
            if (this.cart && this.cart.classList.contains(CLASS_IS_EMPTY)) {
              this.cart.classList.remove(CLASS_IS_EMPTY);
            }
            if (!this.error) {
              this.submitButton.removeAttribute(ATTR_ARIA_DISABLED);
            }
            this.querySelector(SELECTOR_LOADING_SPINNER).classList.add(CLASS_HIDDEN);
            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

   
      // ============================================================================
      // Submit Button Management
      // ============================================================================
      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton || !this.submitButtonText) return;
        
        if (disable) {
          this.submitButton.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
          if (text) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute(ATTR_DISABLED);
          this.updateSubmitButtonText();
        }
      }

      updateSubmitButtonText() {
        if (!this.submitButton || !this.submitButtonText) return;

        const price = this.submitButton.getAttribute(ATTR_DATA_PRICE);
        const showPriceInButton = this.submitButton.getAttribute(ATTR_DATA_SHOW_PRICE_IN_BUTTON) === 'true';
        
        if (price && showPriceInButton) {
          const originalPrice = this.submitButton.getAttribute(ATTR_DATA_ORIGINAL_PRICE);
          const showOriginalPriceInButton =
            this.submitButton.getAttribute(ATTR_DATA_SHOW_ORIGINAL_PRICE_IN_BUTTON) === 'true';
          const showDualPrice =
            showOriginalPriceInButton &&
            originalPrice &&
            Number(originalPrice) > Number(price);

          if (showDualPrice) {
            const formattedOriginal = this.formatPrice(originalPrice);
            const formattedDiscounted = this.formatPrice(price);
            this.submitButtonText.innerHTML = `${window.variantStrings.addToCart} <span class="${CLASS_BUTTON_PRICE}"><span class="original-price">${formattedOriginal}</span> ${formattedDiscounted}</span>`;
          } else {
            const formattedPrice = this.formatPrice(price);
            this.submitButtonText.innerHTML = `${window.variantStrings.addToCart} <span class="${CLASS_BUTTON_PRICE}">${formattedPrice}</span>`;
          }
        } else {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      formatPrice(price) {
        return window.formatMoney ? window.formatMoney(price) : `$${price / 100}`;
      }

      // ============================================================================
      // Form Controls
      // ============================================================================
      get variantIdInput() {
        return this.form.querySelector(SELECTOR_ID_INPUT);
      }

      getButtons() {
        const $root = $(this);
        if (!$root.length) return $();
        return $root.find(SELECTOR_FORM_BTN);
      }

      getSubmitButton() {
        const $root = $(this);
        if (!$root.length) return $();
        return $root.find(SELECTOR_FORM_BTN_SUBMIT);
      }

      getVariantElements() {
        const $root = $(this);
        return {
          labels: $root.find(SELECTOR_VARIANT_LABEL),
          inputs: $root.find(SELECTOR_VPC_CARD_INPUT)
        };
      }

      disableButton() {
        const $buttons = this.getButtons();
        const { labels, inputs } = this.getVariantElements();
        
        let hasChanges = false;
        if ($buttons.length) {
          $buttons.prop('disabled', true);
          hasChanges = true;
        }
        if (labels.length) {
          labels.css('pointer-events', 'none').addClass(CLASS_DISABLED);
          hasChanges = true;
        }
        if (inputs.length) {
          inputs.prop(ATTR_DISABLED, true);
          hasChanges = true;
        }
        return hasChanges;
      }

      enableButton() {
        const $buttons = this.getButtons();
        const { labels, inputs } = this.getVariantElements();
        
        let hasChanges = false;
        if ($buttons.length) {
          $buttons.prop(ATTR_DISABLED, false);
          hasChanges = true;
        }
        if (labels.length) {
          labels.css('pointer-events', '').removeClass(CLASS_DISABLED);
          hasChanges = true;
        }
        if (inputs.length) {
          inputs.prop(ATTR_DISABLED, false);
          hasChanges = true;
        }
        return hasChanges;
      }

      clearForm() {
        if (!this.form) return false;
        this.form.reset();
        // Dispatch change event after form reset to notify listeners
        this.dispatchChangeEvent(CHANGE_TYPES.FORM);
        return true;
      }

      clearCroppingNotes() {
        if (!this.form) return false;
        const croppingNotesFields = this.form.querySelectorAll('textarea[data-cropping-notes]');
        if (croppingNotesFields.length > 0) {
          croppingNotesFields.forEach(field => {
            field.value = '';
          });
          this.dispatchChangeEvent(CHANGE_TYPES.CUSTOM_DATA);
          return true;
        }
        return false;
      }

      // ============================================================================
      // Prop accessors (data-value-type="color|size|…")
      // ============================================================================
      /** Read checked/selected value for any prop key */
      getProp(prop) {
        if (!prop) return null;
        const $root = $(this);
        const selector = `[${ATTR_DATA_VALUE_TYPE}="${prop}"]`;
        let $input = $root.find(`${selector}:checked`);
        if ($input.length) {
          return $input.attr(ATTR_DATA_VALUE) || null;
        }

        // Fallback: options[Prop] radio name
        const escaped = String(prop).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const nameRe = new RegExp(`^options\\[${escaped}\\]$`, 'i');
        $input = $root.find('input[type="radio"]').filter((i, el) => {
          const name = $(el).attr('name') || '';
          return nameRe.test(name) && $(el).is(':checked');
        });
        if ($input.length) {
          const value = $input.val();
          return value ? String(value).toLowerCase().trim() : null;
        }
        return null;
      }

      getCurrentColor() {
        return this.getProp('color');
      }

      getCurrentSize() {
        return this.getProp('size');
      }

      getAvailableOptions(optionType) {
        const $root = $(this);
        const $inputs = $root.find(`[${ATTR_DATA_VALUE_TYPE}="${optionType}"]`);
        const values = [];
        
        $inputs.each(function() {
          const value = $(this).attr(ATTR_DATA_VALUE);
          if (value && !values.includes(value)) {
            values.push(value);
          }
        });
        
        return values;
      }

      // ============================================================================
      // Product Information Methods
      // ============================================================================
      getProductTitle() {
        if (!this.form) return '';

        // Get from JSON data (data-product-info script tag)
        try {
          const productDataScript = document.querySelector('script[data-product-info]');
          if (productDataScript) {
            const productData = JSON.parse(productDataScript.textContent);
            if (productData?.product?.title) {
              return productData.product.title;
            }
          }
        } catch (e) {
          // Ignore parse errors
        }

        return '';
      }

      getCurrentVariantId() {
        if (!this.form) return null;
        return this.variantIdInput?.value || null;
      }

      getCurrentSku() {
        if (!this.form) return '';

        // Get from JSON data (data-product-info script tag)
        try {
          const productDataScript = document.querySelector('script[data-product-info]');
          if (productDataScript) {
            const productData = JSON.parse(productDataScript.textContent);
            const variantId = this.getCurrentVariantId();
            if (variantId && productData?.variants && Array.isArray(productData.variants)) {
              const variant = productData.variants.find(v => String(v.id) === String(variantId));
              if (variant?.sku) {
                return variant.sku;
              }
            }
          }
        } catch (e) {
          // Ignore parse errors
        }

        return '';
      }

      getCurrentTemplate() {
        if (!this.form) return '';

        // Get from hidden input field properties[_template]
        const templateInput = this.form.querySelector('input[name="properties[_template]"]');
        if (templateInput?.value) {
          return templateInput.value;
        }

        return '';
      }

      /**
       * Programmatic select for any data-value-type prop (color, fabric, size, …).
       */
      updateProp(prop, value, options = {}) {
        if (!prop || value == null || value === '') return false;
        const propName = String(prop);
        const propValue = String(value);

        const $root = $(this);
        let $input = $root.find(
          `[${ATTR_DATA_VALUE_TYPE}="${propName}"][${ATTR_DATA_VALUE}="${propValue}"]`
        );

        // Fallback: options[Prop] radio by value
        if (!$input.length) {
          const escaped = propName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const nameRe = new RegExp(`^options\\[${escaped}\\]$`, 'i');
          $input = $root.find('input[type="radio"]').filter((i, el) => {
            const name = $(el).attr('name') || '';
            if (!nameRe.test(name)) return false;
            const v = String($(el).val() || '').toLowerCase().trim();
            return v === propValue.toLowerCase().trim();
          });
        }

        if (!$input.length) return false;

        $input.prop('checked', true);

        if (window.ProductFormPropUI?.setActiveForInput) {
          window.ProductFormPropUI.setActiveForInput(this, $input.get(0));
        } else if (propName === 'color') {
          $root.find(SELECTOR_COLOR_LABEL).removeClass(CLASS_SWATCH_ACTIVE);
          $root.find(`${SELECTOR_COLOR_LABEL}[${ATTR_DATA_COLOR_VALUE}="${propValue}"]`)
            .addClass(CLASS_SWATCH_ACTIVE);
        }

        $input.trigger('change', options.source ? [{ source: options.source }] : []);

        const variantSelects = $root.find(SELECTOR_VARIANT_SELECTS)[0];
        if (variantSelects) {
          variantSelects.dispatchEvent(new Event('change', { bubbles: true }));
        }

        return true;
      }

      updateColor(colorValue, options = {}) {
        return this.updateProp('color', colorValue, options);
      }

      getFormDataObject() {
        if (!this.form) return null;
        
        const formData = new FormData(this.form);
        
        // Add custom data to FormData as properties
        Object.keys(this.customData).forEach(key => {
          const value = this.customData[key];
          if (value != null) {
            formData.append(`properties[${key}]`, String(value));
          }
        });
        
        // Convert FormData to object, handling multiple values for same key
        const result = {};
        for (const [key, value] of formData.entries()) {
          if (key in result) {
            // Convert to array if multiple values exist
            result[key] = Array.isArray(result[key]) 
              ? [...result[key], value]
              : [result[key], value];
          } else {
            result[key] = value;
          }
        }
        
        return result;
      }
    }
  );

  // Static API
  window.ProductFormCustom = window.ProductFormCustom || {};
  
  // Expose EVENTS constant
  window.ProductFormCustom.EVENTS = ProductFormCustom.EVENTS;
  
  // Get product-form-custom instance by form ID (finds form by ID, returns parent product-form-custom)
  const getInstanceById = (formId) => {
    if (!formId) return null;
    const formElement = document.getElementById(formId);
    return formElement?.closest(ELEMENT_NAME) || null;
  };
  
  // Get instance by form ID
  window.ProductFormCustom.id = getInstanceById;
  
  // Get all instances on page as object { formId: instance }
  window.ProductFormCustom.getAll = () => {
    const instances = {};
    document.querySelectorAll(ELEMENT_NAME).forEach(element => {
      const form = element.querySelector(SELECTOR_FORM);
      if (form && form.id) {
        instances[form.id] = element;
      }
    });
    return instances;
  };
  
  // Static methods with form ID parameter
  window.ProductFormCustom.enableButton = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.enableButton?.() || false;
  };
  
  window.ProductFormCustom.disableButton = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.disableButton?.() || false;
  };
  
  window.ProductFormCustom.clearForm = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.clearForm?.() || false;
  };
  
  window.ProductFormCustom.clearCroppingNotes = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.clearCroppingNotes?.() || false;
  };
  
  window.ProductFormCustom.getMockup = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.getMockup?.() || null;
  };
  
  window.ProductFormCustom.getOriginalImage = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.getOriginalImage?.() || null;
  };
  
  window.ProductFormCustom.clearImageData = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.clearImageData?.() || false;
  };
  
  window.ProductFormCustom.setCustomData = (formId, key, value) => {
    const instance = getInstanceById(formId);
    return instance?.setCustomData?.(key, value) || false;
  };
  
  window.ProductFormCustom.getCustomData = (formId, key) => {
    const instance = getInstanceById(formId);
    return instance?.getCustomData?.(key) || null;
  };
  
  window.ProductFormCustom.clearCustomData = (formId, key) => {
    const instance = getInstanceById(formId);
    return instance?.clearCustomData?.(key) || false;
  };
  
  window.ProductFormCustom.getFormDataObject = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.getFormDataObject?.() || {};
  };
  
  window.ProductFormCustom.updateProp = (formId, prop, value, options = {}) => {
    const instance = getInstanceById(formId);
    return instance?.updateProp?.(prop, value, options) || false;
  };

  window.ProductFormCustom.updateColor = (formId, colorValue, options = {}) => {
    const instance = getInstanceById(formId);
    return instance?.updateColor?.(colorValue, options) || false;
  };
  
  window.ProductFormCustom.getCurrentColor = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.getCurrentColor?.() || null;
  };
  
  window.ProductFormCustom.getCurrentSize = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.getCurrentSize?.() || null;
  };

  window.ProductFormCustom.getProp = (formId, prop) => {
    const instance = getInstanceById(formId);
    return instance?.getProp?.(prop) || null;
  };

  /** Wire cart property keys — call from orchestrator init({ form }) */
  window.ProductFormCustom.setAssets = (formId, assets) => {
    const instance = getInstanceById(formId);
    return instance?.setAssets?.(assets) || false;
  };

  window.ProductFormCustom.hasRequiredAssets = (formId) => {
    const instance = getInstanceById(formId);
    if (!instance?.hasRequiredAssets) return true;
    return instance.hasRequiredAssets();
  };

  window.ProductFormCustom.missingRequiredAssets = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.missingRequiredAssets?.() || [];
  };

  window.ProductFormCustom.getAssets = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.getAssets?.() || [];
  };

  window.ProductFormCustom.getCurrentVariantId = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.getCurrentVariantId?.() || null;
  };

  window.ProductFormCustom.getAvailableOptions = (formId, optionType) => {
    const instance = getInstanceById(formId);
    return instance?.getAvailableOptions?.(optionType) || [];
  };

  window.ProductFormCustom.getProductTitle = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.getProductTitle?.() || '';
  };

  window.ProductFormCustom.getCurrentSku = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.getCurrentSku?.() || '';
  };

  window.ProductFormCustom.getCurrentTemplate = (formId) => {
    const instance = getInstanceById(formId);
    return instance?.getCurrentTemplate?.() || '';
  };
}
